import jojoAccount from '@jojo/account-sdk';
import JsBridge from '@woulsl-app/js-bridge';
import wx from 'jweixin-sdk-official';
import qs from 'query-string';
import { Cookies } from 'react-cookie';

import server, { envs, isJoJo, isJoJoUp } from '@/config/base';
import { isJoJoUp as isJoJoUpV2, miniAppId } from '@/config/base';
import { IS_NEW_ACCOUNT_AUTH } from '@/config/const';

import { polarisAppIds } from './constants';
import { platformLimitTypes, shopSource } from './objMapps';
import { isShopLogin, toAuthrize } from './toAuthorize';

const cookies = new Cookies();
export const qsParse = () => {
  return qs.parse(window.location.search.replace(/^\?/, ''));
};

/**
 * 微信小程序内嵌浏览器
 * @returns {boolean|boolean}
 */
export const isWXMiniAppBrowser = () => {
  const ua = window.navigator.userAgent;
  const reg = /miniprogram/i;

  return reg.test(ua);
};

/**
 * 当前环境是否是 阅读好心情小程序（测试环境），叫叫儿童阅读小程序（线上）
 * @returns {boolean|boolean}
 */
export const isInJoJoReadMiniApp = () => {
  const ua = window.navigator.userAgent;
  return isWXMiniAppBrowser() && ua.includes(miniAppId.jojo);
};

/**
 * 检测浏览器环境是否是jojo app 包含识字app
 * eg：
 */
export const isJoJoAllBrowser = () => {
  const ua = window.navigator.userAgent;
  const reg = /JoJoVersion|JoJoWebViewSymbol/i;
  return reg.test(ua);
};

/**
 * 获取平台信息0App 1微信 2小程序 3其他
 * @returns number
 */
export const getCurrPlatform = async (report = false): Promise<number> => {
  try {
    const isMiniApp = isWXMiniAppBrowser() || (await getIsMiniprogramEnv());
    if (isMiniApp) {
      return 2;
    }

    if (isJoJoAllBrowser()) {
      const ua = window.navigator.userAgent;
      if (/JoJoSherlock/gim.test(ua)) {
        // 新识字APP
        return report ? 3 : 5;
      }
      if (/huiben/gim.test(ua)) {
        // 绘本
        return report ? 3 : 6;
      }

      if (/pinyin/gim.test(ua)) {
        // 叫叫拼音app
        return report ? 3 : 7;
      }

      if (/sherlock/gim.test(ua)) {
        // 叫叫老识字app
        return report ? 3 : 8;
      }

      if (/JoJoCalculate/gim.test(ua)) {
        // 叫叫口算app
        return report ? 3 : 4;
      }

      return report ? 3 : 0;
    }

    if (isWXBrowser()) {
      return 1;
    }
    return report ? 10 : 3;
  } catch (e) {
    return report ? 10 : 3;
  }
};

export const getCurrPlatformText = async (): Promise<string> => {
  const platform = await getCurrPlatform();
  return [
    'APP内',
    '微信内',
    '微信小程序',
    '其他',
    '口算APP',
    '新识字APP',
    '绘本APP',
    '拼音APP',
    '老识字APP'
  ][platform];
};

export const debounceFn = (delay = 100) => {
  let timer: any;
  return (fn: () => void) => {
    clearTimeout(timer);
    timer = 0;
    timer = setTimeout(() => {
      fn();
    }, delay);
  };
};

export const clickLimitFn = (time = 500) => {
  let t1: any = null;
  return (fn = Function.prototype) => {
    if (t1 == null) {
      t1 = new Date().getTime();
    } else {
      const t2 = new Date().getTime();
      if (t2 - t1 < time) {
        t1 = t2;
        return;
      } else {
        t1 = t2;
      }
    }
    if (fn) {
      fn();
    }
  };
};

export function getCacheTraceCode() {
  const useType = sessionStorage.getItem('l9UpdateC1UseType') || undefined;
  let traceCode;
  if (useType === 'UPGRADE_CHINESE') {
    traceCode = localStorage.getItem('traceCode');
    if (traceCode) {
      traceCode = decodeURIComponent(traceCode);
    }
  }
  return traceCode;
}

export function getQuery(search: string) {
  const newSearch = decodeURIComponent(search);
  const reg = /([^?&]+)=([^&]+)/g;
  const res: Record<string, string> = {};
  newSearch.replace(reg, (_, k, v): string => {
    if (k) {
      //
      res[k] = v;
    }

    return '';
  });

  return res;
}

export function getHostAndPath(url: string) {
  return url && url.replace(/([^?&]+)=([^&]+)/g, '');
}

/**
 * 判断操作系统
 */
export function osVersion() {
  const ua = window.navigator.userAgent;
  // const ua = 'Mozilla/5.0 (iPhone; CPU iPhone OS 13_3_1 like Mac OS X) AppleWebKit/603.1.30 (KHTML, like Gecko)
  // const ua = 'Mozilla/5.0 (iPad; CPU OS 9_2_1 like Mac OS X) AppleWebKit/601.1.46 (KHTML, like Gecko) Mobile/13D15
  // MicroMessenger/7.0.9(0x17000929) NetType/WIFI Language/zh_CN';
  // Version/10.0 Mobile/14E304 Safari/602.1';
  const reg = /(?:iphone|ipad)[^\d]+os\s(\d+)(?:_\d+)?/i;
  if (reg.test(ua)) {
    const res = reg.exec(ua);
    if (res && res[1]) {
      return parseInt(res[1], 10);
    }
  }
  return null;
}

/**
 * 检测浏览器环境是否是jojo app
 * eg：
 */
export const isJoJoBrowser = () => {
  const ua = window.navigator.userAgent;
  const reg = /JoJoVersion/i;
  const newReg = /JoJoWebViewVersion/i;
  return reg.test(ua) || newReg.test(ua);
};
/**
 * 检测浏览器环境是否是jojo阅读 app
 * eg：
 */
export const isJoJoReadBrowser = () => {
  const ua = window.navigator.userAgent;
  const reg = /JoJoAppFrom\/read/i;
  return isJoJoBrowser() && reg.test(ua);
};

/**
 * 叫叫app内新开webview打开
 * @param targetUrl
 * @returns
 */
export const getWebviewUrl = (targetUrl = '') => {
  const regHttp = /^https?:\/\//;
  if (regHttp.test(targetUrl) && isJoJoReadBrowser()) {
    return `tinman-router://cn.tinman.jojoread/webview?url=${encodeURIComponent(targetUrl)}`;
  }
  return targetUrl;
};
/**
 * 检测浏览器环境是否是微信
 * eg：
 */
export function isWXBrowser() {
  const ua = window.navigator.userAgent;
  const reg = /MicroMessenger/i;
  return reg.test(ua);
}

/**
 * 检测浏览器环境是否是普通浏览器
 * 需排除 微信环境（小程序内嵌浏览器、微信浏览器）、APP环境（APP内嵌浏览器）
 */
export const isBrowser = () => {
  if (!isWXBrowser() && !isJoJoAllBrowser()) {
    return true;
  }
  return false;
};

/**
 * 检测浏览器环境是否是支付宝
 * eg：
 */
export function isALiBrowser() {
  const ua = window.navigator.userAgent;
  const reg = /AlipayClient/i;
  return reg.test(ua);
}
/**
 * 检测浏览器环境是否是钉钉
 * eg：
 */
export function isDDBrowser() {
  const ua = window.navigator.userAgent;
  const reg = /DingTalk/i;
  return reg.test(ua);
}
/**
 * 检测浏览器环境是否为小程序webview
 * eg：
 */
export const getIsMiniprogramEnv = () =>
  new Promise((resolve) => {
    if (isWXBrowser()) {
      // ios的ua中无miniProgram，但都有MicroMessenger（表示是微信浏览器）
      wx.miniProgram.getEnv((res: any) => {
        resolve(Boolean(res.miniprogram));
      });
    } else {
      resolve(false);
    }
  });
/**
 * 小程序内webview跳转到小程序
 * eg：
 */
export const miniprogramNavigateTo = ({
  url,
  params = {},
  onFinish = Function.prototype,
  type = 'navigateTo'
}: any) => {
  const path = Object.keys(params).length > 0 ? `${url}?${qs.stringify(params)}` : url;
  console.log(path);
  if (type === 'redirectTo') {
    wx.miniProgram.redirectTo({ url: path, complete: onFinish });
  } else {
    wx.miniProgram.navigateTo({ url: path, complete: onFinish });
  }
};

/**
 * 小程序内webview跳转到小程序
 * eg：
 */
export const miniprogramSwitchTab = ({ url }: { url: string }) => {
  wx.miniProgram.switchTab({ url });
};

/**
 * 只是微信app内嵌浏览器，非小程序或其他浏览器
 * @returns {boolean|boolean}
 */
export const isWXAppBrowser = () => {
  const ua = window.navigator.userAgent;
  const reg = /miniprogram/i;

  return !reg.test(ua) && isWXBrowser();
};

export function isLessThenIOS9() {
  const version = osVersion();
  return version && version <= 9;
}

export const closest = (el: any, selector: string) => {
  let newEl = el;
  const matchesSelector =
    newEl.matches ||
    newEl.webkitMatchesSelector ||
    newEl.mozMatchesSelector ||
    newEl.msMatchesSelector;
  while (newEl) {
    if (matchesSelector.call(newEl, selector)) {
      return newEl;
    }
    newEl = newEl.parentElement;
  }
  return null;
};

/**
 * 点击穿透bug fix
 * @param e
 */
export const onWrapTouchStart = (e: Event) => {
  // fix touch to scroll background page on iOS
  if (!/iPhone|iPod|iPad/i.test(navigator.userAgent)) {
    return;
  }
  const pNode = closest(e.target, '.am-modal-content');
  if (!pNode) {
    e.preventDefault();
  }
};

/**
 * 触摸点和离开点与X轴所成的角度
 *
 * 根据弧长计算公式：n * Math.PI * r / 180度 = l
 * [n]角度，[r]半径，[l]弧长
 *
 * 此处 l / r = theta = Math.atan2(angY, angX)，即圆心角的弧度表示
 * n = theta * 180度 / Math.PI = 圆心角的度数表示
 * @param angX
 * @param angY
 * @returns {number}
 */
export const getAngle = (angX: number, angY: number) => {
  return (Math.atan2(angY, angX) * 180) / Math.PI;
};

/**
 * 根据接触和离开判断方向 1向上 2向下 3向左 4向右 0未发生滑动（[Math.abs][4]）
 * @param startX 起点x轴坐标
 * @param startY 起点y轴坐标
 * @param endX 终点x轴坐标
 * @param endY 终点y轴坐标
 * @returns {number}
 */
export const getDirection = (startX: number, startY: number, endX: number, endY: number) => {
  const angX = endX - startX;
  const angY = endY - startY;
  let result = 0;

  const ratio = window.devicePixelRatio;
  const moveDistance = ratio * 60;

  // 如果滑动距离太短
  if (Math.abs(angX) < moveDistance && Math.abs(angY) < moveDistance) {
    return result;
  }

  const angle = getAngle(angX, angY);
  if (angle >= -135 && angle <= -45) {
    result = 1;
  } else if (angle > 45 && angle < 135) {
    result = 2;
  } else if ((angle >= 135 && angle <= 180) || (angle >= -180 && angle < -135)) {
    result = 3;
  } else if (angle >= -45 && angle <= 45) {
    result = 4;
  }

  return result;
};

// 上一次点击的时间戳，默认0
let lastClickTime = 0;
/**
 * 防重复点击
 * @param func
 * @returns {function(...[*]=)}
 */
export const fastClickOptimize = (func: () => void) => {
  const currentClickTime = Date.now();

  if (currentClickTime - lastClickTime < 1000) {
    return () => {};
  }

  return () => {
    lastClickTime = Date.now();
    func();
  };
};

/**
 * 判断是否有emoji
 * @param value
 * @returns {boolean}
 */
export const hasEmoji = (value: string) =>
  /\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDE4F]/g.test(value);

/**
 * @returns {enum} //3:app 2:miniprogram 1:wx h5
 */
export const getEnvEnum = async () => {
  if (isJoJoBrowser()) {
    return 3;
  }
  const isInMiniprogram = await getIsMiniprogramEnv();
  if (isInMiniprogram) {
    return 2;
  }
  if (isWXBrowser()) {
    return 1;
  }
  return 1;
};

/**
 * @returns 扫码后获取app内浏览器环境
 */
export const getAppBrowserEnv = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  if (ua.includes('micromessenger')) {
    return 'weixin';
  }
  if (ua.includes('alipayclient')) {
    return 'alipay';
  }
  return false;
};

export const _formateData = (data: Record<string, any>) => {
  const newData = Object.entries(data)
    .filter(([, value]: any) => value || value === 0 || value === false)
    .map(([key, value]: any) => ({ [key]: value }))
    .reduce((accumulator: any, current: any) => ({ ...accumulator, ...current }), {});
  return newData;
};

export const getHostWithEnv = (envName: string) => {
  const hostArr = window.location.origin.split('.');
  // 判断是否已经带有环境标识，注：正式环境域名不包含pro
  if (!envs.includes(hostArr[1])) {
    const envStr = envName === 'pro' ? '' : envName;
    hostArr.splice(1, 0, envStr);
  }
  const newHost = hostArr.filter((o) => o).join('.');
  return newHost;
};

// 接入了微信和支付宝检测的app版本
export const appEditon = {
  'dev.shusheng.JoJoRead': '1.14.1',
  'com.shusheng.JoJoRead': '1.14.1',
  'com.jojoread.JoJoSherlock': '1.15.0',
  'com.jojoread.huiben': '2.8.24',
  'com.jojoread.JoJoCalculate': '1.4.0',
  'com.tinmanarts.JoJoSherlock': '2.75.0',
  'com.mohezi.fairyland': '2.2.0'
};

// 比较版本号
export const isHigerVersion = (version1: string, version2: string) => {
  let isHigher = false;
  if (version1 && version2) {
    const newVersion1 = version1.split('.').map((el) => Number(el));
    const newVersion2 = version2.split('.').map((el) => Number(el));
    let diff = 0;
    do {
      isHigher =
        diff === 2 ? newVersion2[diff] >= newVersion1[diff] : newVersion2[diff] > newVersion1[diff];
      diff += 1;
    } while (diff <= 2 && !isHigher && newVersion2[diff - 1] === newVersion1[diff - 1]);
  }
  return isHigher;
};

// 直接登录
let loginTimer: any = null;
export const jojoAppDirectLogin = async (url = '') => {
  try {
    if (isJoJoReadBrowser() && isJoJoUp) {
      await jojoAccount.logout();
      // 叫叫app中使用jojoup链接时
      const redirectUrl: string = toAuthrize({
        mode: 1,
        authBizType: 3
      });
      window.location.replace(redirectUrl);
    } else {
      const bridge = isJoJoBrowser() ? new JsBridge() : {};
      clearTimeout(loginTimer);
      loginTimer = setTimeout(async () => {
        let packageName = isJoJoUp ? 'mp.mohezi.JoJoUp' : 'mp.tinman.JoJoRead';
        const ua = window.navigator.userAgent;
        const [{ status, data }, bundleID] = await Promise.all([
          bridge.call('nativeLogin'),
          getAppBundleId()
        ]);
        if (/huiben/gim.test(ua)) {
          packageName = 'com.jojoread.huiben';
        } else if (bundleID) {
          packageName = bundleID;
        }
        if (status === 200 && data && data.authToken) {
          window.location.href = `${server.UC_API_URL}/page/appWeb/portal/appWebLogin?authToken=${
            data.authToken
          }&packageName=${packageName}&targetUrl=${encodeURIComponent(
            url || window.location.href
          )}`;
        }
      }, 300);
    }
  } catch (e) {
    console.error('ERROR===', e);
  }
};

export const isAuditGuestLogin = async () => {
  let audit = false;
  let guest = false;
  if (isIosApp() && isJoJoReadBrowser()) {
    const bridge = new JsBridge() || {};
    if (bridge.canUseBridge && bridge.canUseBridge()) {
      const deviceInfo = await bridge.call('getDeviceInfo');
      if (deviceInfo?.status === 200 && deviceInfo?.data) {
        audit = deviceInfo?.data?.iosAudit === '1';
        guest = deviceInfo?.data?.iosAuditVisitor === '1';
      }
    }
  }
  if (audit && guest && isIosApp() && isJoJoReadBrowser()) {
    jojoAppDirectLoginNoGuest();
    return true;
  } else {
    return false;
  }
};
// 游客模式下，需要调用另外的登录原生页面
export const jojoAppDirectLoginNoGuest = async (url = '') => {
  try {
    if (isJoJoReadBrowser() && isJoJoUp) {
      await jojoAccount.logout();
      // 叫叫app中使用jojoup链接时
      const redirectUrl: string = toAuthrize({
        mode: 1,
        authBizType: 3
      });
      window.location.replace(redirectUrl);
    } else {
      const bridge = isJoJoBrowser() ? new JsBridge() : {};
      clearTimeout(loginTimer);
      loginTimer = setTimeout(async () => {
        const { status, data } = await bridge.call('nativeOnlyLogin');
        if (status === 200 && data && data.authToken) {
          window.location.href = `${server.UC_API_URL}/page/appWeb/portal/appWebLogin?authToken=${
            data.authToken
          }&packageName=mp.tinman.JoJoRead&targetUrl=${encodeURIComponent(
            url || window.location.href
          )}`;
        }
      }, 300);
    }
  } catch (e) {
    console.error('ERROR===', e);
  }
};
// 跨域名跳转（保持登录）
export const crossDomainJumpWithLogin = (url = '') => {
  try {
    const authToken = cookies.get('authToken');
    window.location.href = `${
      server.UC_BASE_API_URL
    }/page/appWeb/portal/appWebLogin?authToken=${authToken}&packageName=mp.tinman.JoJoRead&targetUrl=${encodeURIComponent(
      url || window.location.href
    )}`;
  } catch (e) {
    console.error('ERROR===', e);
  }
};
// APP矩阵包名路径
export const appSchemeHostList = {
  'com.jojoread.huiben': 'anibook-router://com.jojoread.huiben',
  'com.jojoread.JoJoCalculate': 'jojocalculate-router://cn.tinman.jojocalculate',
  'com.jojoread.JoJoSherlock': 'jojosherlock-router://cn.tinman.jojosherlock',
  'com.tinmanarts.JoJoSherlock': 'sherlock-router://com.tinmanarts.jojosherlock'
};

export const getAppBundleId = async () => {
  try {
    const bridge = isJoJoBrowser() ? new JsBridge() : {};
    const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
    if (canUseBridge) {
      const appInfo = await bridge.appInfo();
      const { data: { bundleID = '' } = {} } = appInfo || {};
      return bundleID;
    }
    return '';
  } catch (error) {
    return '';
  }
};

export const getShopSource = async () => {
  let source = 'H5';
  if (isJoJoBrowser()) {
    // 如果是在APP内
    const bridge = new JsBridge();
    const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
    source = 'APP';
    if (canUseBridge) {
      try {
        const appInfo = await bridge.appInfo();
        const { data: { bundleID = '' } = {} } = appInfo || {};
        source = shopSource[bundleID as keyof typeof shopSource] || 'APP';
      } catch (error) {
        console.log(error);
      }
    }
  } else {
    const isMiniProgram = await getIsMiniprogramEnv();
    if (isMiniProgram) {
      source = 'MINIPROGRAM';
      try {
        if (navigator.userAgent.match(/wx[0-9a-z]+$/)) {
          const appId = navigator.userAgent.match(/wx[0-9a-z]+$/)![0];
          source = shopSource[appId as keyof typeof shopSource] || 'MINIPROGRAM';
        }
      } catch (error) {
        console.log(error);
      }
    }
  }
  return source;
};

export const getAppSubscribeReturnUrl = async () => {
  try {
    const bridge = isJoJoBrowser() ? new JsBridge() : {};
    const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
    if (canUseBridge) {
      const appBundleId = await getAppBundleId();
      if (appBundleId && appSchemeHostList[appBundleId as keyof typeof appSchemeHostList]) {
        return `${appSchemeHostList[appBundleId as keyof typeof appSchemeHostList]}/market`;
      }
    }
    return '';
  } catch (error) {
    return '';
  }
};

export const isPolaris = () => {
  const ua = navigator.userAgent;
  return (
    polarisAppIds.findIndex((appid) => {
      return ua.indexOf(appid) >= 0;
    }) >= 0 || window.location.href.includes('miniName=northstar')
  );
};
export const getPlatformInfo = async () => {
  const platformLimitId = await getShopSource();
  const env = await getEnvEnum();
  const platformLimitType = platformLimitTypes[env];
  return { platformLimitId, platformLimitType };
};

export const getIsInJOJOFairyland = async () => {
  const appBundleId = await getAppBundleId();
  return appBundleId === 'com.mohezi.fairyland';
};

/**
 * 是否在JOJOUP环境中
 * @returns {boolean}
 */
export const isJOJOUP = async () => {
  const bridge = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
  // app包名
  const packageName = 'com.read.box.book';
  // 非app环境域名
  // const domain = 'jojoup.cn';

  let isJojoUp = false;
  if (isJoJoBrowser()) {
    // app环境判断包名
    if (canUseBridge) {
      try {
        const appInfo = await bridge.appInfo();
        const {
          data: { bundleID }
        } = appInfo || {};
        isJojoUp = bundleID === packageName;
      } catch (error) {
        console.log('bridge ready error', error);
      }
    }
  } else {
    // 非app环境判断域名 （域名暂未审核通过，先不做域名判断）
    // if (window?.location?.hostname?.includes(domain)) {
    //   isJojoUp = true;
    // }
  }
  return isJojoUp || isJoJoUpV2;
};

/**
 * 是否是jojoup APP
 * @returns {boolean}
 */
export const isJoJoUpApp = () => {
  const ua = window.navigator.userAgent;
  if (isJoJoAllBrowser()) {
    if (/flawless/gim.test(ua)) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * 是否是矩阵 APP (叫叫识字，绘本，口算，汉字大冒险)
 * @returns {boolean}
 */
export const isMatrixApp = async () => {
  if (isJoJoAllBrowser()) {
    const isHanZi = await isHanziApp();
    const isKouSuan = await isKouSuanApp();
    if (isHuiBenApp() || isJojoShiZiApp() || isHanZi || isKouSuan) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};
/**
 * 是否是绘本 APP
 * @returns {boolean}
 */
export const isHuiBenApp = () => {
  const ua = window.navigator.userAgent;
  if (isJoJoAllBrowser()) {
    return /huiben/gim.test(ua);
  } else {
    return false;
  }
};
/**
 * 是否是叫叫识字 APP（识字3.0）
 * @returns {boolean}
 */
export const isJojoShiZiApp = () => {
  const ua = window.navigator.userAgent;
  if (isJoJoAllBrowser()) {
    return /JoJoSherlock/gim.test(ua);
  } else {
    return false;
  }
};
/**
 * 是否是汉字大冒险 APP(新识字)
 * @returns {boolean}
 */
export const isHanziApp = async () => {
  // 汉字大冒险没有ua可以用于判断，所以用包名来判断，矩阵包名、UA文档：https://alidocs.dingtalk.com/i/nodes/r1R7q3QmWe7LqyrKi9NrYoGpJxkXOEP2?corpId=dingabb5c5b29d35a9dc35c2f4657eb6378f&utm_medium=im_card&iframeQuery=sheet_range%3Ds1_4_6_1_1%26utm_medium%3Dim_card%26utm_source%3Dim&utm_scene=team_space&utm_source=im
  const appName = await getAppName();
  return appName === 'com.jojoread.jojosherlock';
};

/**
 * 是否是口算APP
 * @returns {boolean}
 */
export const isKouSuanApp = async () => {
  const appName = await getAppName();
  return appName === 'com.jojoread.jojocalculate';
};

/**
 * 是否是叫叫儿童阅读APP 鸿蒙版
 * @returns {boolean}
 */
export const isJoJoReadAppForHM = async () => {
  const appName = await getAppName();
  return appName === 'com.shusheng.hm.jojoread';
};

/**
 * 是否是 华为联运包
 * @returns {boolean}
 */
export const isHuaweiLianyun = async () => {
  const bridge = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
  let isHuawei = false;

  // 华为联运跳转华为支付收银台
  if (isJoJoBrowser() && canUseBridge) {
    try {
      const appInfo = await bridge.appInfo();
      const { data: { jointOperationsChannel = '' } = {} } = appInfo || {};
      isHuawei = jointOperationsChannel === 'huawei';
    } catch (error) {
      console.log('bridge ready error', error);
    }
  }
  return isHuawei;
};

/**
 * 获取包名
 */
export const getAppName = async () => {
  const bridge = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
  let curBundleId = '';
  if (isJoJoBrowser() && canUseBridge) {
    try {
      const appInfo = await bridge.appInfo();
      const { data: { bundleID = '' } = {} } = appInfo || {};
      curBundleId = bundleID?.toLowerCase();
    } catch (error) {
      console.log('bridge ready error', error);
    }
  }
  return curBundleId;
};

/**
 * 获取包名枚举值
 */
export const getAppEnum = async () => {
  const bridge = isJoJoBrowser() ? new JsBridge() : {};
  const canUseBridge = bridge.canUseBridge ? bridge.canUseBridge() : false;
  const packMap: any = {
    // 叫叫学院
    'com.shusheng.jojoread': '1',
    'dev.shusheng.jojoread': '1',
    // 叫叫识字
    'com.tinmanarts.jojosherlock': '6',
    'com.jojoread.jojosherlock': '4',
    // 叫叫绘本
    'com.jojoread.huiben': '2',
    'com.jojoread.jojocalculate': '5',
    'com.mohezi.fairyland': '7'
    // 叫叫绘画
    // 'com.tinmanarts.painter': '',
    // 'com.jojoread.readcamp': '',
  };
  let curBundleId = '';
  if (isJoJoBrowser() && canUseBridge) {
    // 获取APP包名
    try {
      const appInfo = await bridge.appInfo();
      const { data: { bundleID = '' } = {} } = appInfo || {};
      curBundleId = bundleID?.toLowerCase();
    } catch (error) {
      console.log('bridge ready error', error);
    }
  }
  return packMap?.[curBundleId] || '';
};

/**
 * 是否为ios app 环境
 */
export const isIosApp = () => {
  const ua = window.navigator.userAgent;
  if (isJoJoBrowser()) {
    return /iPhone|iPad|iPod/i.test(ua);
  }
  return false;
};

/**
 * 是否为android app 环境
 */
export const isAndroidApp = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  if (isJoJoBrowser()) {
    return /android/.test(ua);
  }
  return false;
};

/**
 * 是否登录：微信浏览器中
 */
export const isLoginInWxBrowser = () => {
  let isLogin = false;
  const authToken = cookies.get('authToken');
  console.log(isWXAppBrowser(), authToken, 'isWXAppBrowser', cookies.get(IS_NEW_ACCOUNT_AUTH));
  if (isWXAppBrowser()) {
    // 在微信浏览器环境内，已登录非新授权
    const isNewAccountAuth = cookies.get(IS_NEW_ACCOUNT_AUTH);
    if (authToken && isNewAccountAuth === 'yes') {
      isLogin = true;
    }
  }
  return isLogin;
};

/**
 * 是否登录：普通浏览器中
 */
export const isLoginInBrowser = () => {
  let isLogin = false;
  if (!isWXBrowser() && !isJoJoAllBrowser()) {
    const authToken = cookies.get('authToken');
    if (authToken) {
      isLogin = true;
    }
  }
  return isLogin;
};

// 未登录情况替换为无授权接口
// api请求放在了baseUrl上
export const replaceNoRighyApiBaseUrl = ({
  action,
  baseUrl
}: {
  action: string;
  baseUrl: string;
}) => {
  let _baseUrl = baseUrl;
  const _action = action;

  if (_action && !isShopLogin()) {
    try {
      const shopActionReg = [
        /\/shop-product-list\/categoryTree$/,
        /\/shop-product-list\/getProducts$/,
        /\/shop-product-list\/getActivity$/,
        /\/shop\/detail$/,
        /\/shopv\/announcement-list$/,
        /\/shop\/more-product-list$/,
        /\/shop\/search-product-list$/,
        /\/shop\/banner-detail$/,
        /\/shop\/announcement-detail$/
      ];
      const isShopApi = shopActionReg.some((el: RegExp) => el.test(action));
      if (isShopApi) {
        if (!_baseUrl) _baseUrl = server.MALL_BASE_URL;
        _baseUrl = _baseUrl?.replace('/api/fe', '/api/open');
      }
    } catch (e) {
      console.log(e);
    }
  }
  return { _action, _baseUrl };
};

// 店铺新接口替换请求路径
export const replaceNewShopBaseUrl = ({ action, baseUrl }: { action: string; baseUrl: string }) => {
  let _baseUrl = baseUrl;
  const _action = action;
  if (_action) {
    try {
      const shopActionReg = [
        /\/shop-product-list\/categoryTree$/,
        /\/shop-product-list\/getProducts$/,
        /\/shop-product-list\/getActivity$/,
        /\/shop\/detail$/,
        /\/shopv\/announcement-list$/,
        /\/shop\/more-product-list$/,
        /\/shop\/search-product-list$/,
        /\/shop\/banner-detail$/,
        /\/shop\/search-records$/,
        /\/shop\/announcement-detail$/,
        /\/activities\/\{activityId\}\/gifts$/,
        /\/activities\/\{activityId\}\/gifts\/\{giftId\}$/,
        /\/shop\/delete-search-record$/,
        /\/shop\/save-module-visit-record$/
      ];
      const isShopApi = shopActionReg.some((el: RegExp) => el.test(action));
      if (isShopApi && isJoJo) {
        const newServerUrl = server.SHINE_BASE_URL.replace(/fe$/, 'api');
        // 命中新店铺配置
        _baseUrl = `${newServerUrl}/fe`;
      }
    } catch (e) {
      console.log(e);
    }
  }
  return { _action, _baseUrl };
};
/**
 * 是否是X5内核
 */
export const isX5Browser = () => {
  if (!process.browser) {
    return false;
  }
  const ua = window.navigator.userAgent.toLocaleLowerCase();
  return isJoJoBrowser() && /mqqbrowser/.test(ua);
};

/**
 * 获取当前系统枚举值
 * @returns {string}
 */
export const getSystem = () => {
  enum System {
    IOS = 'IOS',
    ANDROID = 'ANDROID',
    HMOS = 'HMOS',
    OTHER = 'OTHER'
  }
  const ua = navigator.userAgent.toLowerCase();
  if (/android/.test(ua) && !/hmos/.test(ua)) {
    return System.ANDROID;
  } else if (/hmos/.test(ua)) {
    return System.HMOS;
  } else if (/iphone|ipad|ipod|ios|macintosh/.test(ua)) {
    return System.IOS;
  } else {
    return System.OTHER;
  }
};
